/**
 * Shared types + constants for the trading mutation hooks.
 *
 * The on-chain trade/boost/thesis/claim orchestration layer lives in the
 * SDK; the host supplies bindings (Zustand stores, toast, share, telemetry,
 * i18n, currency formatter, network) via `<TradingHostProvider value={…}>`
 * and the SDK hooks read them through `useTradingHostContext()`.
 */

import type { BN } from '@coral-xyz/anchor';
import type { QueryClient } from '@tanstack/react-query';
import type { SigningDeps } from '../../../auth/turnkey/signing-types.js';
import type {
  ActiveWallet,
  BuildResult,
  SubmissionOptions,
  SubmissionResult,
  TurnkeyDeps,
} from '../../../tx-engine/index.js';
import type { TxMessageKey } from './tx-message-keys.js';

export type { TxMessageKey } from './tx-message-keys.js';
export { TX_MESSAGE_KEY_PATHS, TX_MESSAGE_KEYS } from './tx-message-keys.js';

/**
 * Imperative submit function returned by the platform-specific
 * `useSubmitTransaction` hook.
 *
 * Inlined here (instead of imported from `../auth/tx/use-submit-transaction.js`)
 * because that path is platform-specific — `web/auth/tx/use-submit-transaction.ts`
 * and `react-native/auth/tx/use-submit-transaction.ts` each export their own
 * (identical) `SubmitTransactionFn`. A shared file can't resolve to either, so
 * we duplicate the 3-line alias here. The underlying types (`BuildResult`,
 * `SubmissionOptions`, `SubmissionResult`) ARE platform-neutral, imported from
 * the tx-engine barrel above.
 */
export type SubmitTransactionFn = (
  buildResult: BuildResult,
  options: SubmissionOptions,
) => Promise<SubmissionResult>;

export interface TradeEntityReconciliationTarget {
  readonly signature: string;
  readonly expectedEventCount: number;
}

/** Submission truth plus every successful transaction that may index trades.
 * Failed swaps can still carry a successful sell leg for reconciliation. */
export type TradeMutationResult = SubmissionResult & {
  readonly tradeEntityTargets?: readonly TradeEntityReconciliationTarget[];
};

// ── Mutation params ──────────────────────────────────────────────────────────

export interface BuyTradeParams {
  marketAddress: string;
  outcomeIndex: number;
  outcomeMint: string;
  /**
   * Curve input amount in D6. Buys are fees-on-top: this amount enters the
   * bonding curve unchanged and the buy fee + prize-pool fee are charged on top.
   */
  amountInD6: BN;
  /**
   * Exact quote-token wallet debit in D6 for optimistic net cost basis.
   * Bonding-curve buys include fees charged on top; CP buys equal `amountInD6`.
   */
  walletDebitD6: BN;
  /** Fee-excluded quote amount that entered the pricing curve (D6). */
  feeExcludedQuoteInputD6: BN;
  /**
   * Estimated outcome tokens received (D6). Used only to optimistically patch
   * the host's per-outcome balance cache on confirmation — not part of the
   * on-chain instruction. Slippage means the realised fill may differ slightly;
   * the host reconciles to chain truth shortly after.
   */
  outcomeTokensOutD6: BN;
  /** Exact D6 user intent used for toast/share/telemetry presentation. */
  desiredAmountD6: BN;
  /** Retry metadata in BPS; market and winner buys pass a zero minimum output. */
  slippageBps: number;
  suggestedSlippageBps: number;
  marketPhase: 'bonding_curve' | 'amm';
  activeWallet: ActiveWallet;
  signingDeps?: SigningDeps;
  /**
   * Platform's share of the base trading fee in USDC D6 (string), computed by
   * the host via `splitFloorFee(baseFee, feeSplitBackersBps)` at quote time.
   * `'0'` on graduated/AMM markets (pool + creator fees only, no platform cut).
   * Omitted = unknown; trade_success then carries no revenue attribution.
   */
  platformFeeD6?: string;
  /** Fires in the same confirmation turn as the success toast. Host-only UI
   * consumers may use this to mirror the confirmed state without waiting for
   * the slower authoritative-signature reconciliation promise. */
  onConfirmed?: () => void;
}

/** Raw canonical position-accounting fields consumed by checked market math. */
export interface PositionAccountingDto {
  balanceD6: string;
  remainingCostBasisNetD6: string;
  claimRightsBalanceD6: string;
  realizedPnlD6: string;
  realizedCostBasisD6: string;
}

export interface SellTradeParams {
  marketAddress: string;
  outcomeIndex: number;
  outcomeMint: string;
  /** Tokens to sell (D6) */
  tokensNeededD6: BN;
  /** Expected net USDC output after fees (D6) — used for position accounting */
  expectedNetUsdcOutD6: BN;
  /** Exact gross quote-output/user intent (D6), used by toast and share payloads. */
  grossQuoteAmountD6: BN;
  /** Fee-excluded quote proceeds used by gross P&L accounting. */
  feeExcludedQuoteOutputD6: BN;
  /**
   * Conviction penalty this sell pays for exiting a non-leading outcome (D6),
   * from the same quote that feeds the form's "Conviction fee" line. Zero on
   * the AMM and on a leading outcome. P&L books it against the fee-free result,
   * so the optimistic row needs it; the other exit fees stay out of P&L.
   * Omitted means none was charged.
   */
  sellPenaltyFeeD6?: BN;
  /** Retry metadata in BPS; market and winner sells pass a zero minimum output. */
  slippageBps: number;
  suggestedSlippageBps: number;
  marketPhase: 'bonding_curve' | 'amm';
  activeWallet: ActiveWallet;
  signingDeps?: SigningDeps;
  /** Market display name, threaded into the realised-PnL share text. */
  marketTitle?: string | undefined;
  /**
   * Canonical indexed state used by the checked position-accounting API.
   * `null` keeps the trade executable but fails toast/viral PnL closed.
   */
  positionAccounting: PositionAccountingDto | null;
  /** Platform's share of the base trading fee in USDC D6 (string). See BuyTradeParams. */
  platformFeeD6?: string;
  /** See BuyTradeParams.onConfirmed. */
  onConfirmed?: () => void;
}

export interface SwapTradeParams {
  marketAddress: string;
  fromOutcomeIndex: number;
  toOutcomeIndex: number;
  fromOutcomeMint: string;
  toOutcomeMint: string;
  /** Tokens to sell from the `from` outcome (D6) — the sell-leg input. */
  tokensD6: BN;
  /** Expected net USDC proceeds from the sell leg (D6). The instruction has no
   *  minimum output; the host applies slippage only when sizing the buy budget. */
  expectedSellNetUsdcOutD6: BN;
  /** Fee-excluded gross proceeds from the swap sell leg (D6). */
  sellGrossQuoteOutputD6: BN;
  /** Conviction penalty the swap's sell leg pays (D6). See `SellTradeParams`. */
  sellPenaltyFeeD6?: BN;
  /** Curve input for the buy leg (D6) — reverse-solved from the floored sell
   *  proceeds by the shared market-math budget quote. Buys are fees-on-top. */
  buyAmountInD6: BN;
  /** Exact fee-inclusive buy-leg debit for optimistic net cost basis. */
  buyWalletDebitD6: BN;
  /** Fee-excluded quote amount entering the swap buy curve (D6). */
  buyGrossQuoteInputD6: BN;
  /** Estimated buy-side tokens out (D6) for the optimistic `to`-outcome balance
   *  patch; the instruction has no minimum output. */
  estimatedTokensOutD6: BN;
  /** Always `'bonding_curve'` for swaps (the Swap tab is hidden on graduated/AMM
   *  markets); threaded to `submitTx` for parity with buy/sell. */
  marketPhase: 'bonding_curve' | 'amm';
  /** Used to reserve a buy budget; bonding-curve swap instructions have zero minima. */
  slippageBps: number;
  /** Whether an oversized atomic swap may fall back to ordered sell then buy legs. */
  executionPolicy: 'atomic_only' | 'atomic_then_sequential';
  activeWallet: ActiveWallet;
  signingDeps?: SigningDeps;
  /** Platform fee across BOTH legs in USDC D6 (string). See BuyTradeParams. */
  platformFeeD6?: string;
}

export type BoostTradeParams = {
  marketAddress: string;
  activeWallet: ActiveWallet;
  signingDeps?: SigningDeps;
} & (
  | {
      /** Exact on-chain boost amount in D6. */
      boostAmountD6: string;
      boostAmountUsdc?: never;
    }
  | {
      /** Legacy human-readable USDC input used by the web client. */
      boostAmountUsdc: number;
      boostAmountD6?: never;
    }
);

export interface ClaimBackerFeesParams {
  marketAddress: string;
  /** Backer-share burn amount in D6 (already-D6 since share mint has 6 decimals). */
  burnAmountD6: string;
  activeWallet: ActiveWallet;
  signingDeps?: SigningDeps;
}

export interface ClaimPrizeParams {
  marketAddress: string;
  /** Claim-rights burn amount in D6 (the winning outcome's prize-rights balance). */
  burnAmountD6: string;
  activeWallet: ActiveWallet;
  signingDeps?: SigningDeps;
}

// ── Internal context ─────────────────────────────────────────────────────────

export interface MutationContext {
  traceId: string;
  startedAt: number;
  abortController: AbortController;
}

export interface TradeContext {
  traceId: string;
  instruction: string;
  walletType: string;
  walletAddress: string;
  marketAddress: string;
  outcomeIndex: number;
  latencyMs: number;
  /** Trade notional in USDC D6 (string) for revenue analytics. Omitted = unknown. */
  tradeNotionalD6?: string;
  /** Platform's share of the base trading fee in USDC D6 (string). */
  platformFeeD6?: string;
}

export interface ThesisTradeContext {
  outcomeIndex: number;
  direction: 'buy' | 'sell';
  amountD6: string;
  outcomeLabel: string | null;
}

export interface TradeShareContext {
  /**
   * D6-encoded USDC amount as a string (BigInt-safe). For buys this is
   * the gross amount in; for sells, the expected net USDC out.
   */
  amountD6: string;
  /** Exact D6 outcome-share amount shown in the confirmed trade toast. */
  shareAmountD6: string;
  outcomeIndex: number;
}

// ── Host-injected deps (no platform-specific code inside the SDK hook body) ──

/**
 * Translator surface — mirrors `useTranslations('tx')` from next-intl.
 *
 * The call signature is narrowed to `TxMessageKey` so every static message the
 * trading helpers fire is compile-checked against the `TX_MESSAGE_KEYS`
 * contract; a new or mistyped key fails typecheck in this package until it is
 * registered there (and, downstream, added to the host `en.json`).
 *
 * `format` is the permissive escape hatch for runtime-dynamic keys — chain
 * error codes resolved by `resolveErrorMessage`, which guards with `has` and
 * falls back to the raw key. It is the only path allowed to pass an arbitrary
 * string; static copy must go through the typed call signature.
 */
export type TxTranslator = {
  (key: TxMessageKey, values?: Record<string, string | number>): string;
  format: (key: string, values?: Record<string, string | number>) => string;
  has: (key: string) => boolean;
};

/** Minimal toast input the SDK builds — mirrors `frontend/src/lib/ui/toast.ts:ShowToastParams`. */
export interface ShowToastInput {
  id: string;
  message: string;
  description?: string;
  variant?: 'default' | 'success' | 'error' | 'info' | 'warning' | 'loading';
  duration?: number;
  action?: { label: string; onClick: () => void };
  cancel?: { label: string; onClick?: () => void };
  actionLayout?: 'inline';
  /** Realised PnL to render colour-coded. `amountD6` is the signed D6 value
   *  (host derives tone from sign + dust threshold); `formatted` is the
   *  host-formatted, currency-aware signed string (e.g. `+$12.34`). */
  pnl?: { amountD6: string; formatted: string };
}

/** Minimal share-modal request the SDK builds for a trade-share toast. */
export interface ShareTradeRequest {
  surface: {
    kind: 'trade';
    signature: string;
    marketPubkey: string;
    userPubkey: string;
    direction: 'buy' | 'sell' | 'swap';
    amountD6: string;
    outcomeIndex: number;
  };
  url: string;
  text: string;
  title: string;
}

/** TX-submission Zustand store surface — only the methods the SDK calls. */
export interface TxSubmissionStoreApi {
  startSubmission: (traceId: string, instructionName: string, maxAttempts?: number) => void;
  updateStage: (
    traceId: string,
    stage: 'preflight' | 'signing' | 'confirmed' | 'cancelled' | 'submitted',
    extra?: { attempt?: number },
  ) => void;
  setSignature: (traceId: string, signature: string) => void;
  setError: (traceId: string, error: { code: string; userMessageKey: string }) => void;
}

/** Minimal logger surface — Better Stack or console; same shape as `TxEngineLogger`. */
export interface TradeLogger {
  info: (msg: string, ctx?: Record<string, unknown>) => void;
  warn: (msg: string, ctx?: Record<string, unknown>) => void;
  error: (msg: string, ctx?: Record<string, unknown>) => void;
}

export interface ThesisCapability {
  openDialog: (
    marketAddress: string,
    signature: string,
    walletAddress: string,
    tradeContext: ThesisTradeContext | null,
    tradeId?: string,
  ) => void;
  /** Reconcile the authoritative signature for an already-open trade dialog. */
  reconcileDialogSignature: (tradeId: string, signature: string) => void;
  addLocal: (signature: string, content: string) => void;
}

export interface TradeSharingCapability {
  buildUrl: (
    signature: string,
    ctx: {
      marketPubkey: string;
      userPubkey: string;
      direction: 'buy' | 'sell' | 'swap';
      amountD6: string;
      outcomeIndex: number;
    },
  ) => string;
  getBaseUrl: () => string;
  openModal: (request: ShareTradeRequest) => void;
}

export interface SellDeeplinkCapability {
  readEntryPoint: () => string | undefined;
}

export interface OptimisticBalanceCapability {
  patch: (args: { walletAddress: string; outcomeMint: string; rawDeltaD6: bigint }) => void;
}

export type OptimisticPositionUpdater = (args: {
  walletAddress: string;
  market: string;
  outcomeIndex: number;
  outcomeMint: string;
  tokenDeltaD6: bigint;
  quoteD6: bigint;
  grossQuoteD6: bigint;
  /**
   * Conviction penalty this leg pays (D6); zero on buys and on any exit that
   * is not a non-leading bonding-curve sell. P&L subtracts it from the
   * fee-free result, so the optimistic row must add it to the booked penalty
   * instead of waiting for the authoritative snapshot.
   */
  penaltyD6: bigint;
  walletDebitD6: bigint | null;
  isAmm: boolean;
}) => void;

export type OptimisticPositionsCapability =
  | { upsert: OptimisticPositionUpdater; prepare?: never }
  | { prepare: () => OptimisticPositionUpdater; upsert?: never };

/**
 * Shared dependency surface threaded through every trade-mutation hook.
 *
 * The host builds one instance and supplies it through `<TradingHostProvider
 * value={…}>` at the providers root; each SDK trade hook reads it via
 * `useTradingHostContext()`. The SDK never imports Zustand, Sonner, the
 * frontend's Statsig wrapper, or any host-vendor module — every binding flows
 * through this interface.
 */
export interface TradeMutationDeps {
  // ── SDK-resolved primitives, forwarded by the host ─────────────────────────
  submitTx: SubmitTransactionFn;
  turnkeyDeps: TurnkeyDeps;
  queryClient: QueryClient;
  /** Solana connection resolver — host re-resolves on network toggle. */
  getConnection: () => import('@solana/web3.js').Connection;

  // ── Display & i18n ─────────────────────────────────────────────────────────
  t: TxTranslator;
  /** Formats a human-readable USDC amount for toast messages. */
  formatBalance: (n: number) => string;
  /** Formats an unsigned exact D6 USDC value without a Number round trip. */
  formatBalanceD6: (d6: string) => string;
  /** Formats an exact D6 outcome-share value without a Number round trip. */
  formatShareD6: (d6: string) => string;
  /** Formats a signed D6 USDC value, currency-aware (e.g. `+$12.34` / `−$5.00`).
   *  Used for the realised-PnL display + share text. */
  formatBalanceSigned: (d6: string) => string;

  // ── Toast surface ──────────────────────────────────────────────────────────
  showToast: (params: ShowToastInput) => void;
  dismissToast: (id: string) => void;
  /** Compose the explorer URL for `signature` on the active network. */
  getExplorerUrl: (signature: string) => string;
  /** Open an external URL. Web: `window.open(url, '_blank')`. RN: `Linking.openURL(url)`. */
  openExternalUrl: (url: string) => void;
  recordTradeSubmission?: (submission: {
    traceId: string;
    submissionTraceId: string;
    isFinalLeg: boolean;
  }) => void;

  // ── Telemetry ──────────────────────────────────────────────────────────────
  /** Typed-analytics emitter (e.g. `@/events:onEvent`). */
  emitEvent: (name: string, payload: Record<string, unknown>) => void;
  /** Better Stack action tracker (e.g. `@/lib/telemetry:trackAction`). */
  trackAction: (action: string, meta: Record<string, unknown>) => void;
  /** Error reporter (e.g. `@/lib/telemetry:reportError`). */
  reportError: (err: unknown, ctx: Record<string, unknown>) => void;
  logger: TradeLogger;

  // ── Network resolution ─────────────────────────────────────────────────────
  /** Returns the active network name (e.g. `'mainnet' | 'devnet'`). */
  getActiveNetwork: () => string;
  /**
   * Active network's USDC (quote) mint base58. The NATS early-confirm watch
   * for an AMM SELL keys on the swap's OUTPUT mint, which for a sell is the
   * quote/USDC mint (the indexer publishes `morfi.amm.{outputMint}.user.*`).
   */
  getUsdcMint: () => string;

  // ── Store-mediated callbacks ──────────────────────────────────────────────
  txSubmission: TxSubmissionStoreApi;

  // ── Cache invalidation — host owns the legacy query keys ──────────────────
  /** Fires from `onSettled` so the host can invalidate its TanStack keys. */
  onMutationSettled: (
    kind: 'buy' | 'sell' | 'swap' | 'boost' | 'claim_backer_fees' | 'claim_prize',
    scope?: {
      readonly marketAddress: string;
      readonly walletAddress: string;
      readonly signature?: string;
    },
  ) => void;
  /**
   * Fires once per confirmed prize or backer-fee claim, after the success
   * toast, so the host can celebrate (web: confetti). `payoutD6` is the exact
   * quote credited, or `null` when the confirmed transaction could not be read.
   */
  onClaimConfirmed?: (claim: {
    readonly kind: 'claim_prize' | 'claim_backer_fees';
    readonly marketAddress: string;
    readonly payoutD6: string | null;
  }) => void;
  /**
   * Optimistically patch the host's per-outcome RPC balance cache after a
   * CONFIRMED trade so the displayed holding value updates instantly (the
   * price is already realtime). `rawDeltaD6` is a signed token delta (D6,
   * base units); the host clamps the result at 0 and recomputes the display
   * balance. The host reconciles to chain truth via a scoped refetch shortly
   * after, so the estimate-vs-fill drift self-heals.
   */
  /**
   * Optimistically upsert the viewer's position row in every cached
   * `useGetPositions` list after a CONFIRMED trade, so a freshly-opened
   * position appears instantly — with NO post-trade polling. This is the
   * only no-poll path that covers a graduated-winner buy (the per-user AMM
   * NATS subject does not exist), and it gives instant feedback for every
   * trade on the originating tab.
   *
   * `tokenDeltaD6` is the SIGNED token delta (positive on buy, negative on
   * sell); `quoteD6` is the unsigned executable quote magnitude (buy: curve / CP
   * input; sell: net proceeds), while `grossQuoteD6` is its fee-excluded P&L
   * counterpart. `walletDebitD6` is the exact buy-side net basis (BC input +
   * fees; CP input unchanged). Sell legs set it to null; a positive token delta
   * with null is rejected at the internal cache boundary.
   * The host upserts by
   * `(wallet, market, outcomeIndex)`. The host maps the checked
   * `@morfi/market-math` result into the optimistic row; NATS re-marks + the
   * next natural refetch reconcile to chain truth.
   */
  thesis?: ThesisCapability;
  tradeSharing?: TradeSharingCapability;
  sellDeeplink?: SellDeeplinkCapability;
  optimisticBalance?: OptimisticBalanceCapability;
  optimisticPositions?: OptimisticPositionsCapability;
}

// ── Toast-id constants (relocated from frontend/src/config/toast-ids.ts) ─────
//
// The SDK builds toast IDs from these prefixes so it doesn't depend on the
// host's full toast-id constants file. They MUST stay byte-identical to the
// frontend's `TOAST_IDS` entries with the same names — duplicating them keeps
// the SDK free of a host import, and the names are validated by toast-id
// uniqueness conventions in the frontend.

export const TRADING_TOAST_IDS = {
  TX_SUBMITTING: 'tx-submitting',
  TX_CONFIRMED: 'tx-confirmed',
  TX_FAILED: 'tx-failed',
  TX_CANCELLED: 'tx-cancelled',
  THESIS_SUBMITTING: 'thesis-submitting',
  THESIS_CONFIRMED: 'thesis-confirmed',
  THESIS_FAILED: 'thesis-failed',
} as const;
